/**
 * Eventos de pago del checkout, con la forma del `dataLayer` de GTM (ENA-1806).
 *
 * El iframe no carga GTM ni Firebase. Arma el objeto una vez y lo entrega a
 * transportes registrados. Hoy el transporte `host` hace postMessage: el shell
 * web lo empuja a `dataLayer` (GTM) y, en ENA-1803, la app lo mapea a Firebase.
 * Registrar otro transporte no cambia los call sites.
 */
import { isDesktop } from '../platform';

/** Canal del embed: checkout web (`?source=desktop`) vs app nativa. */
export type PaymentAnalyticsSource = 'web' | 'app';

export function resolvePaymentAnalyticsSource(): PaymentAnalyticsSource {
  return isDesktop ? 'web' : 'app';
}

export interface PaymentAnalyticsItemInput {
  item_name: string;
  item_id: string;
  price: number;
  quantity: number;
  item_brand?: string;
  item_variant?: string;
  categories?: string[];
}

/** Snapshot que el host inyecta (`analytics-context` o `window.__INIT__.analytics`). */
export interface PaymentAnalyticsContext {
  currency: string;
  value: number;
  salesChannel: string;
  /** Crudo. Se normaliza a `food` | `non-food` | `externo`. */
  businessUnit?: string;
  items: PaymentAnalyticsItemInput[];
}

export interface AddPaymentInfoCustomPush {
  event: 'add_payment_info_custom';
  source: PaymentAnalyticsSource;
  orderFormPaymentType: string;
  ecommerce: {
    currency: string;
    value: number;
    payment_type: string;
    items: Array<Record<string, string | number>>;
    p_site_business_unit: string;
    salesChannel: string;
  };
}

/** Emisor del medio elegido. Aditivo y en la raíz (fuera de `ecommerce`); hoy solo `carrefour`. */
export type PaymentIssuer = 'carrefour';

export interface SelectPaymentInfoPush {
  event: 'select_payment_info';
  source: PaymentAnalyticsSource;
  ecommerce: { payment_box: string };
  /** Solo presente si el medio elegido es la tarjeta de crédito Carrefour (Mastercard). */
  payment_issuer?: PaymentIssuer;
}

export interface EventClickPush {
  event: 'event_click';
  source: PaymentAnalyticsSource;
  click_type: string;
  click_detail: string;
}

/** Pixel UA opcional. No se emite: Tag 830 lee `orderFormPaymentType` del push custom. */
export interface LegacyPaymentPush {
  event: 'payment';
  source: PaymentAnalyticsSource;
  orderFormPaymentType: string;
}

export type DataLayerPush =
  | AddPaymentInfoCustomPush
  | SelectPaymentInfoPush
  | EventClickPush
  | LegacyPaymentPush;

export type AnalyticsTransport = {
  id: string;
  send: (push: DataLayerPush) => void;
};

const transports = new Map<string, AnalyticsTransport>();

export function registerAnalyticsTransport(transport: AnalyticsTransport): void {
  transports.set(transport.id, transport);
}

export function resetAnalyticsTransports(): void {
  transports.clear();
}

/** Misma acción, N proveedores. Un transporte que falla no frena a los demás. */
export function emitPaymentAnalytics(push: DataLayerPush): void {
  for (const transport of transports.values()) {
    try {
      transport.send(push);
    } catch (error) {
      console.error(`[payment-analytics] transporte ${transport.id}`, error);
    }
  }
}

export function normalizeBusinessUnit(
  value: string | undefined | null,
): 'food' | 'non-food' | 'externo' {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'food' || normalized === 'cart-food') return 'food';
  if (
    normalized === 'non-food' ||
    normalized === 'nonfood' ||
    normalized === 'electro' ||
    normalized === 'cart-electro'
  ) {
    return 'non-food';
  }
  return 'externo';
}

function toEcommerceItem(item: PaymentAnalyticsItemInput): Record<string, string | number> {
  const ecommerceItem: Record<string, string | number> = {
    item_name: item.item_name,
    item_id: item.item_id,
    price: item.price,
    item_brand: item.item_brand ?? '',
    item_variant: item.item_variant ?? '',
    quantity: item.quantity,
  };
  (item.categories ?? [])
    .map((category) => category.trim())
    .filter(Boolean)
    .forEach((category, index) => {
      const key = index === 0 ? 'item_category' : `item_category${index + 1}`;
      ecommerceItem[key] = category;
    });
  return ecommerceItem;
}

export function buildAddPaymentInfoCustom(
  context: PaymentAnalyticsContext,
  paymentType: string,
): AddPaymentInfoCustomPush {
  return {
    event: 'add_payment_info_custom',
    source: resolvePaymentAnalyticsSource(),
    orderFormPaymentType: paymentType,
    ecommerce: {
      currency: context.currency || 'ARS',
      value: context.value,
      payment_type: paymentType,
      items: context.items.map(toEcommerceItem),
      p_site_business_unit: normalizeBusinessUnit(context.businessUnit),
      salesChannel: context.salesChannel,
    },
  };
}

export function buildSelectPaymentInfo(
  paymentBox: string,
  issuer?: PaymentIssuer,
): SelectPaymentInfoPush {
  return {
    event: 'select_payment_info',
    source: resolvePaymentAnalyticsSource(),
    ecommerce: { payment_box: paymentBox },
    ...(issuer ? { payment_issuer: issuer } : {}),
  };
}

export function buildEventClick(clickType: string, clickDetail: string): EventClickPush {
  return {
    event: 'event_click',
    source: resolvePaymentAnalyticsSource(),
    click_type: clickType,
    click_detail: clickDetail,
  };
}

export function buildLegacyPaymentPush(orderFormPaymentType: string): LegacyPaymentPush {
  return {
    event: 'payment',
    source: resolvePaymentAnalyticsSource(),
    orderFormPaymentType,
  };
}

/**
 * Orden de payment: `add_payment_info_custom` una vez (medio inicial), después
 * los `select_payment_info` que el usuario haya hecho, aunque el contexto del
 * carrito llegue más tarde.
 */
export class PaymentAnalyticsSession {
  private context: PaymentAnalyticsContext | null = null;
  private customType: string | null = null;
  private customEmitted = false;
  private queuedBoxes: Array<{ paymentBox: string; issuer?: PaymentIssuer }> = [];
  private giftCodes = new Set<string>();

  constructor(private readonly emit: (push: DataLayerPush) => void) {}

  setContext(context: PaymentAnalyticsContext): void {
    this.context = context;
    this.flush();
  }

  /** Medio ya elegido al montar (mobile / default). No es un click del usuario. */
  noteInitialMethod(paymentType: string): void {
    if (!paymentType || this.customType) return;
    this.customType = paymentType;
    this.flush();
  }

  selectMethod(paymentType: string, paymentBox: string, issuer?: PaymentIssuer): void {
    if (!paymentType || !paymentBox) return;
    if (!this.customType) this.customType = paymentType;
    if (!this.customEmitted) {
      this.queuedBoxes.push({ paymentBox, issuer });
      this.flush();
      return;
    }
    this.emit(buildSelectPaymentInfo(paymentBox, issuer));
  }

  click(clickType: string, clickDetail: string): void {
    if (!clickType || !clickDetail) return;
    this.emit(buildEventClick(clickType, clickDetail));
  }

  giftCode(redemptionCode: string): void {
    const code = redemptionCode.trim();
    if (!code || this.giftCodes.has(code)) return;
    this.giftCodes.add(code);
    this.click('agregar_codigo', code);
  }

  private flush(): void {
    if (this.customEmitted || !this.context || !this.customType) return;
    this.emit(buildAddPaymentInfoCustom(this.context, this.customType));
    this.customEmitted = true;
    const queued = this.queuedBoxes.splice(0);
    for (const { paymentBox, issuer } of queued) {
      this.emit(buildSelectPaymentInfo(paymentBox, issuer));
    }
  }
}
